import React from 'react';
import { RiCloseLine } from 'react-icons/ri';
import { IoCheckmarkCircle } from 'react-icons/io5';
import * as translator from '../../utils/translator';
import useIsMobile from '../../customHooks/useIsMobile';

interface PharmacyDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: () => void;
  selectedService?: string;
  showSelectButton?: boolean;
  pharmacy: {
    name: string;
    address: string;
    opening_hours?: string;
    location?: { lat: number; lng: number };
    is_external_pharmacy: boolean;
    external_integration_code?: string;
  } | null;
}

export const PharmacyDetailsModal: React.FC<PharmacyDetailsModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedService,
  showSelectButton = true,
  pharmacy,
}) => {
  const language = localStorage.getItem('language') || 'de';
  const isMobile = useIsMobile();

  if (!isOpen || !pharmacy) return null;

  // Determine which badge to show - only in prescription_only service
  const isOnlyPrescriptionService = selectedService === 'prescription_only';
  const showUnknownStockBadge =
    isOnlyPrescriptionService &&
    pharmacy.is_external_pharmacy &&
    !pharmacy.external_integration_code;
  const showPayOnceBadge = isOnlyPrescriptionService && !pharmacy.is_external_pharmacy;

  const compact = isMobile;

  return (
    <div
      className={`
        inline-flex w-fit flex-col items-start
        bg-white rounded-lg overflow-visible
        max-w-[min(92vw,22rem)]
      `}
    >
      {/* Header */}
      <div className={`relative w-full min-w-0 ${compact ? 'p-2' : 'p-3'}`}>
        <h3 className={`text-[#00302E] font-medium break-words whitespace-normal pr-8 ${compact ? 'text-sm' : 'text-base'}`}>
          {pharmacy.name}
        </h3>
        <button
          onClick={onClose}
          className={`absolute top-2 right-2 flex items-center justify-center rounded-full cursor-pointer border-none bg-transparent hover:bg-black/5 transition-colors z-10 ${compact ? 'w-6 h-6' : 'w-7 h-7'}`}
          aria-label="Close"
          type="button"
        >
          <RiCloseLine className={`text-[#00302E] ${compact ? 'w-4 h-4' : 'w-5 h-5'}`} />
        </button>
      </div>

      {/* Content */}
      <div className={`w-full min-w-0 ${compact ? 'px-2 pb-2 space-y-1.5' : 'px-3 pb-3 space-y-3'}`}>
        <p className={`font-roboto text-[#666666] break-words whitespace-normal ${compact ? 'text-[11px] line-clamp-2' : 'text-xs'}`}>
          {pharmacy.address}
        </p>

        {/* Opening Hours – hidden on mobile to fit without scroll */}
        {pharmacy.opening_hours && !compact && (
          <div className="flex flex-col gap-0.5 min-w-0">
            {pharmacy.opening_hours.split(',').map((hour, index) => (
              <span
                key={index}
                className="text-[#666666] text-xs font-roboto font-extralight opacity-50 break-words whitespace-normal"
              >
                {hour.trim().replace(/null-null/, 'Geschlossen')}
              </span>
            ))}
          </div>
        )}

        {/* Badges */}
        {showUnknownStockBadge && (
          <div
            className={`inline-flex items-center gap-1 rounded-md font-semibold whitespace-normal mt-2 break-words ${compact ? 'p-1 text-[10px]' : 'px-1 py-1 text-xs'}`}
            style={{ backgroundColor: '#FDF4E5', color: '#F08E00' }}
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/unKnownstockVector.png`}
              alt={translator.translate(language, 'choose_treatment', 'unknown_stock_modal/title')}
              className={`flex-shrink-0 object-contain ${compact ? 'w-[10px] h-[10px] min-w-[10px] min-h-[10px]' : 'w-4 h-4'}`}
              style={{ filter: 'invert(0.45) sepia(1) saturate(4) hue-rotate(10deg)' }}
            />
            <span className="whitespace-nowrap">{translator.translate(language, 'choose_treatment', 'unknown_stock_modal/title')}</span>
          </div>
        )}

        {showPayOnceBadge && (
          <div
            className={`inline-flex items-center gap-1 my-2 rounded-md font-semibold whitespace-normal break-words ${compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'}`}
            style={{ backgroundColor: '#037D73', color: '#FFFFFF' }}
          >
            <IoCheckmarkCircle className={`flex-shrink-0 ${compact ? 'w-3 h-3' : 'w-4 h-4'}`} />
            <span className="break-words whitespace-normal">
              {translator.translate(language, 'choose_treatment', 'shipping_and_service_selection/pay_once')}
            </span>
          </div>
        )}

        {onSelect && showSelectButton && (
          <div className="flex justify-start">
            <button
              id="pharmacy-details-select-button"
              onClick={onSelect}
              className={`w-fit rounded-md my-2 h-20 text-white font-roboto font-medium border-none cursor-pointer ${compact ? 'min-w-[10rem] px-3 py-3 text-xs' : 'min-w-[12rem] px-4 py-2 text-sm'}`}
              style={{ backgroundColor: '#269C6E', borderRadius: '4.8px' }}
              type="button"
            >
              {translator.translate(language, 'choose_treatment', 'default/choose_pharmacy')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};